import { orderBy, round, sumBy } from 'lodash';

type InputType = {
  allocations: Array<{
    commitmentId: string;
    allocationAmountTcy: number;
    allocationAmountLcy?: number | null;
  }>;
  includedCommitments: Array<{
    isBalancingEntity: boolean;
    _id: string;
  }>;
  valueToBalance: 'allocationAmountTcy' | 'allocationAmountLcy';
  targetAmount: number;
};

const findBalancingEntityIndex = ({
  allocations,
  includedCommitments,
}: {
  allocations: InputType['allocations'];
  includedCommitments: InputType['includedCommitments'];
}) => {
  const balancingCommitment = includedCommitments.find(
    (commitment) => commitment.isBalancingEntity,
  );

  if (!balancingCommitment) {
    return null;
  }

  const idx = allocations.findIndex(
    (alloc) => alloc.commitmentId === balancingCommitment._id,
  );

  return idx >= 0 ? idx : null;
};

const orderAllocationsByLargestFirst = ({
  allocations,
  valueToBalance,
}: {
  allocations: InputType['allocations'];
  valueToBalance: InputType['valueToBalance'];
}) => {
  return orderBy(
    allocations.map((alloc, idx) => ({
      idx,
      value: alloc[valueToBalance] ?? 0,
    })),
    (a) => Math.abs(a.value),
    ['desc'],
  );
};

export default function balanceAllocations({
  allocations,
  includedCommitments,
  targetAmount,
  valueToBalance,
}: InputType) {
  const sumOfAllocations = round(sumBy(allocations, valueToBalance), 2);

  if (sumOfAllocations && sumOfAllocations !== targetAmount) {
    const variance = round(targetAmount - sumOfAllocations, 2);

    const maxAllowedVariance = allocations.length * 0.01;

    if (Math.abs(variance) > maxAllowedVariance) {
      throw new Error(
        `Sum of allocations variance ${Math.abs(
          variance,
        )} exceeds threshold ${maxAllowedVariance} for ${
          allocations.length
        } allocations`,
      );
    }

    const balancingIdx = findBalancingEntityIndex({
      allocations,
      includedCommitments,
    });

    let centsToDistribute = Math.round(variance * 100);

    if (balancingIdx !== null) {
      const totalAdjustment = centsToDistribute * 0.01;
      allocations[balancingIdx][valueToBalance] = round(
        (allocations[balancingIdx][valueToBalance] ?? 0) + totalAdjustment,
        2,
      );
    } else {
      const allocationIndicesByPriority = orderAllocationsByLargestFirst({
        allocations,
        valueToBalance,
      });

      let currentIdx = 0;

      while (
        centsToDistribute !== 0 &&
        currentIdx < allocationIndicesByPriority.length
      ) {
        const idx = allocationIndicesByPriority[currentIdx].idx;
        const adjustment = centsToDistribute > 0 ? 0.01 : -0.01;

        allocations[idx][valueToBalance] = round(
          (allocations[idx][valueToBalance] ?? 0) + adjustment,
          2,
        );

        centsToDistribute += adjustment > 0 ? -1 : 1;
        currentIdx++;
      }
    }
  }
}
